/**
 * Registry central de Custom Elements do tema.
 *
 * - coreDefine(name, ctor): define imediatamente (síncrono, para o núcleo).
 * - registerLazy(name, loader): agenda definição para quando o elemento existir
 *   no DOM. A checagem acontece na DOMContentLoaded e a cada evento
 *   'theme:components:scan'.
 * - scan(): percorre todos os lazy pendentes e define os que têm elemento no
 *   documento. Chamado internamente e exportado para uso em testes.
 *
 * Regra: nenhum módulo fora deste arquivo chama customElements.define()
 * diretamente (exceto os módulos de cart/product que auto-registram, que
 * serão importados dinamicamente por este registry).
 */

/** @type {Map<string, () => Promise<{default: CustomElementConstructor|null}>>} */
const _pending = new Map();

/**
 * Define um custom element imediatamente (núcleo síncrono).
 * @param {string} name
 * @param {CustomElementConstructor} ctor
 */
export function coreDefine(name, ctor) {
  if (!customElements.get(name)) {
    customElements.define(name, ctor);
  }
}

/**
 * Registra um elemento para definição lazy.
 * @param {string} name  tag name do custom element
 * @param {() => Promise<{default: CustomElementConstructor|null}>} loader
 *   função que retorna um dynamic import
 */
export function registerLazy(name, loader) {
  if (customElements.get(name)) return; // já definido (ex: HMR / re-run)
  _pending.set(name, loader);
}

/**
 * Percorre os lazy pendentes e define os que têm presença no DOM atual.
 * É assíncrono: o import() de cada módulo resolve antes do define().
 */
export async function scan() {
  if (_pending.size === 0) return;

  const toDefine = [];
  for (const [name, loader] of _pending) {
    if (document.querySelector(name)) {
      toDefine.push([name, loader]);
    }
  }

  await Promise.all(
    toDefine.map(async ([name, loader]) => {
      _pending.delete(name); // remove antes do await para não disparar duas vezes
      try {
        const mod = await loader();
        if (mod.default && !customElements.get(name)) {
          customElements.define(name, mod.default);
        }
      } catch (err) {
        console.error(`[component-registry] falha ao carregar <${name}>`, err);
        _pending.set(name, loader); // recoloca para retry no próximo scan
      }
    }),
  );
}

// Scan na DOMContentLoaded (cobre o HTML inicial)
//
// ATENÇÃO: component-registry.js é uma DEPENDÊNCIA — é avaliado ANTES de
// components/index.js e main.js no grafo de módulos ES. Quando este código
// roda, _pending ainda está vazio. Por isso:
//
//   readyState 'loading'     → DOMContentLoaded dispara depois que todos os
//     módulos deferidos terminam de avaliar, então _pending já está cheio. ✓
//
//   readyState 'interactive' → módulo deferido avaliado após o parse HTML mas
//     antes do DOMContentLoaded. Não podemos esperar pelo evento (pode nunca
//     mais disparar). queueMicrotask() roda após a cadeia síncrona inteira de
//     módulos ES terminar (mesma task), com _pending já cheio. ✓
//
//   readyState 'complete'    → import dinâmico pós-carregamento.
//     queueMicrotask() funciona pelo mesmo motivo. ✓
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
} else {
  queueMicrotask(() => scan());
}

// Scan a cada notificação de mutação com custom elements
document.addEventListener('theme:components:scan', () => scan());
